import { ROLE } from '@/enums/person';

import type { Credit } from '@/@types/graphql-types';
import { CreditRole, ObjectType } from '@/@types/graphql-types';
import type Genre from '@/interfaces/genre';
import type { TitleDetailNodeEpisode, TitleDetailsNode } from '@/interfaces/title-details-graphql';

import { createThumborFilter } from '@/filters/thumbor';
import { getScorings } from '@/helpers/scoring-helper-graphql';

const PROTOCOL = 'https://';
const SUBDOMAIN = (JW_CONFIG.DOMAIN as string)?.startsWith('appdev') ? '' : 'www.';
const APP_URL = `${PROTOCOL}${SUBDOMAIN}${JW_CONFIG.DOMAIN}`;

const JsonLDHelper = {
	getJsonLdContent,
};

function getJsonLdContent(
	title: TitleDetailsNode,
	genresByShortName: Record<string, Genre>,
	thumborFilter: ReturnType<typeof createThumborFilter>
) {
	const linkedDataContent: any = {
		'@graph': [
			{
				'@context': 'http://schema.org',
				'@type': getType(title.objectType),
				containsSeason: title.objectType === ObjectType.Show ? getSeasons(title) : null,
			},
		],
	};
	// just a shorthand
	if (title?.content) {
		linkedDataContent['@graph'][0] = {
			...linkedDataContent['@graph'][0],
			actor: getActorList(title.content.credits),
			author: getCreditList(title.content.credits, ROLE.WRITER),
			dateCreated: title.content.originalReleaseYear,
			description: title.content.shortDescription,
			director: getCreditList(title.content.credits, ROLE.DIRECTOR),
			genre: getGenresForTitle(genresByShortName, title),
			image: title.content.posterUrl
				? thumborFilter({ url: title.content.posterUrl, imageType: 'poster' })
				: null,
			name: title.content.title,
		};
	}

	let graph = linkedDataContent['@graph'][0];

	// add age rating
	if (title.content.ageCertification) {
		graph.contentRating = title.content.ageCertification;
	}

	// Movie
	if (title.objectType === ObjectType.Movie) {
		graph.duration = getDurationString(title.content.runtime);
	}

	// Show Season
	if (title.objectType === ObjectType.ShowSeason) {
		graph.partOfSeries = {
			'@type': 'TVSeries',
			name: title.content.title,
			url: title.show?.content.fullPath ?? title.content.fullPath,
		};
		graph.seasonNumber = title.content.seasonNumber ?? null;
		graph.episode = title.episodes.map(episode => schemaEpisode(episode));
		graph.numberOfEpisodes = title.totalEpisodeCount ?? title.episodes.length;
	}

	// Add Ratings (if available)
	const { tmdbScore, tmdbPopularity, imdbVotes } = title.content.scoring;
	const [jwRating] = getScorings(title.content.scoring, title.content.externalIds);

	if (tmdbScore != null && tmdbPopularity != null) {
		graph.aggregateRating = {
			'@type': 'AggregateRating',
			itemReviewed: {
				'@type': getType(title.objectType),
				name: title.content.title,
				image: graph.image,
				director: graph.director,
				dateCreated: title.content.originalReleaseYear,
			},
			bestRating: '100',
			ratingCount: imdbVotes,
			ratingValue: jwRating.score,
			ratingExplanation:
				'The JustWatch rating is calculated by weighing and measuring how our users interact with shows & movies across different time periods and countries.',
		};
	}

	if (title.content?.videobusterClips?.length > 0 || title.content?.dailymotionClips?.length > 0) {
		const uploadDate = getVideoUploadDate(title);
		const videoUrlInfo = getVideoUrlInfo(title);
		graph.trailer = {
			'@type': 'VideoObject',
			name: title.content.title,
			description: title.content.shortDescription,
			uploadDate,
			thumbnailUrl:
				title.content?.backdrops.map(el => thumborFilter({ url: el.backdropUrl, imageType: 'backdrop' })) ?? [],
			...videoUrlInfo,
		};
	}

	return linkedDataContent;
}

function getType(type: ObjectType) {
	switch (type) {
		case ObjectType.Movie:
			return 'Movie';
		case ObjectType.Show:
			return 'TVSeries';
		case ObjectType.ShowSeason:
			return 'TVSeason';
		default:
			return null;
	}
}
function getVideoUploadDate(title: TitleDetailsNode) {
	let uploadDate = null;
	const minimumDate = new Date('2021-06-16');
	if (title.content.originalReleaseDate) {
		uploadDate = new Date(title.content.originalReleaseDate);
		uploadDate.setMonth(uploadDate.getMonth() - 1);
	} else if (title.content.originalReleaseYear) {
		uploadDate = new Date(title.content.originalReleaseYear, 11, 31);
	}
	return uploadDate && uploadDate > minimumDate ? uploadDate.toISOString() : minimumDate.toISOString();
}

function getVideoUrlInfo(title: TitleDetailsNode) {
	if (title.content.videobusterClips.length) {
		return { contentUrl: title.content.videobusterClips[0].externalId };
	} else if (title.content.dailymotionClips.length) {
		return {
			contentUrl: 'https://www.dailymotion.com/crawler/video/' + title.content.dailymotionClips[0].externalId,
		};
	}
	return {};
}
function getGenresForTitle(genresByShortName: Record<string, Genre>, title: TitleDetailsNode) {
	return title.content.genres.map(genre => genresByShortName[genre.shortName]?.translation);
}

function getCreditList(credits: Credit[] = [], type: string) {
	return credits.filter(credit => credit.role === type).map(credit => schemaPerson(credit.name));
}

function getActorList(credits: Credit[] | undefined) {
	if (credits == null) return [];

	return credits
		.filter(credit => credit.role === CreditRole.Actor)
		.map(credit =>
			credit.characterName ? schemaPerformanceRole(credit.name, credit.characterName) : schemaPerson(credit.name)
		);
}

function getDurationString(runtime: number | null) {
	if (runtime == null) return null;

	const hours = Math.floor(runtime / 60);
	const minutes = runtime % 60;
	const pad = (n: number) => n.toString().padStart(2, '0');

	// Time must be in ISO8601 format https://en.wikipedia.org/wiki/ISO_8601#Times
	// T13:47:00
	return `T${pad(hours)}:${pad(minutes)}:00`;
}

function getSeasons(title: TitleDetailsNode) {
	if (title.seasons == null) return [];

	return title.seasons.map(season => {
		return {
			'@type': 'TVSeason',
			datePublished: title.content.originalReleaseYear,
			name: season.show?.content.title,
			url: new URL(season.content.fullPath, APP_URL).toString(),
		};
	});
}

const schemaPerson = (name: string) => ({
	'@type': 'Person',
	name: name,
});

const schemaPerformanceRole = (name: string, characterName: string) => ({
	'@type': 'PerformanceRole',
	actor: schemaPerson(name),
	characterName,
});

const schemaEpisode = ({ content: episode }: TitleDetailNodeEpisode) => ({
	'@type': 'Episode',
	name: episode.title,
	episodeNumber: episode.episodeNumber,
	description: episode.shortDescription ?? null,
});

export default JsonLDHelper;
